<template>
  <div id="map" ref="map">
    <div id="basemap" class="mapboxgl-ctrl-top-right mapboxgl-ctrl"
         v-bind:class="{ 'shift_layer_control': zoomEnabled }">
      <div class="mapboxgl-ctrl mapboxgl-ctrl-group basemaps">
        <LayerList
            v-on:transit-layer-change="transitLayerOn = $event"
            v-on:land-use-context-layer-change="landUseContextLayerOn = $event"
            v-on:school-district-layer-change="schoolDistrictLayerOn = $event"
            v-bind:mappedAmenity="mappedAmenity"
            v-bind:data_table="data_table"
        />
      </div>
    </div>
    <div ref="mapgl" class="mapboxgl-map"></div>
    <MapboxLayer
        name="isobands"
        beforeLayerName="road-pedestrian-case"
        v-bind:geoJSON="isobands_poly"
        v-bind:layers="isobandsLayer"
    />
    <MapboxLayer
        name="isobandlines"
        beforeLayerName="waterway-label"
        v-bind:geoJSON="isobands_poly"
        v-bind:layers="isobandlinesLayer"
    />
    <MapboxLayer
        name="boundary"
        v-bind:geoJSON="boundary"
        v-bind:layers="boundaryLayer"
    />
    <MapboxLayer
        name="school_district_boundary"
        v-bind:geoJSON="schoolDistrictBoundaries"
        v-bind:layers="schoolDistrictBoundaryLayer"
    />
    <MapboxLayer_FromTiles
        name="transit_routes"
        beforeLayerName="demographicPoints_circle_0"
        v-bind:interactivity=true
        v-on:featureClick="transitProviderPopup($event)"
        v-bind:layers="transitProvidersLayer"
        v-bind:tiles="[`https://vectortileservices1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/transit_routes/VectorTileServer/tile/{z}/{y}/{x}.pbf`]"
    />
    <MapboxLayer
        name="block_group_2020_time_summary"
        beforeLayerName="place-neighbourhood"
        v-bind:interactivity=true
        v-on:featureClick="bivariatePopup($event)"
        v-bind:layers="bgTravelTimeLayer"
        v-bind:url="bivariateLayersLookup[data_table].url"
    />
    <MapboxLayer_FromTiles
        name="land_use_context"
        v-bind:interactivity=true
        v-on:featureClick="landUseContextPopup($event)"
        v-bind:layers="landUseContextLayer"
        v-bind:tiles="[`https://vectortileservices1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/Land_Use_Context/VectorTileServer/tile/{z}/{y}/{x}.pbf`]"
    />
    <MapboxLayer_FromTiles
        name="all_core_services"
        v-bind:layers="destLayer"
        v-bind:interactivity=true
        v-on:featureClick="popup($event)"
        v-bind:tiles="[`https://vectortileservices1.arcgis.com/xUx8EjNc6egUPYWh/arcgis/rest/services/all_core_services/VectorTileServer/tile/{z}/{y}/{x}.pbf`]"
    />
    <MapboxLayer
        name="tileset"
        v-bind:url='modeNames[data_table].url'
        v-bind:layers='tilesetLayer'
    />
    <MapboxLayer
        name="demographicPoints"
        v-bind:geoJSON="demographicPoints"
        v-bind:layers='demographicLayer'
        v-bind:interactivity="true"
        v-bind:clusterSettings="demographicClusterSettings"
        v-on:featureClick="demographic_update_popup($event)"
    />
    <MapboxLayer
        name="dot_dens_demographics"
        :url="dotDensDemographicURLs[dotDensDemographicColumn]"
        v-bind:layers="dot_dens_demographics"
    />
    <MapboxLayer
        name="jobPoints"
        v-bind:geoJSON="jobPoints"
        v-bind:layers='jobLayer'
        v-bind:interactivity="true"
        v-on:featureClick="job_update_popup($event)"
    />
    <MapboxLayer
        name="voronoi_poly"
        beforeLayerName="road-pedestrian-case"
        v-bind:geoJSON="voronoi_poly"
        v-bind:layers='voronoiLayer'
        v-bind:interactivity="true"
        v-on:featureClick="job_update_popup($event)"
    />
    <MapboxLayer
        name="isoPoints"
        v-bind:geoJSON="isoPoints"
        v-bind:layers="isoPointsLayer"
    />

    <AmenitySelect
        class="map-overlay no-print"
        v-bind:mappedAmenity="mappedAmenity"
        v-bind:amenities="amenities"
        v-bind:amenityNames="amenityNames"
        v-bind:counties="counties"
        v-bind:mcd="mcd"
        v-bind:boundaryNames="boundaryNames"
        v-bind:modeNames="modeNames"
        v-bind:data_tables="data_tables"
        v-bind:data_table="data_table"
        v-bind:demographicColumn="demographicColumn"
        v-bind:demographicColumns="demographicColumns"
        v-bind:dotDensDemographicColumns="dotDensDemographicColumns"
        v-bind:dotDensDemographicColumn="dotDensDemographicColumn"
        v-bind:jobColumn="jobColumn"
        v-bind:jobColumns="jobColumns"
        v-bind:jobColumnNames="jobColumnNames"
        v-bind:startExpanded="startExpanded"
        v-bind:bgSummaryLayerOn="bgSummaryLayerOn"
        v-bind:freeFlow="freeFlow"
        v-on:input="setAmenity($event)"
        v-on:mcdInput="setMcd($event)"
        v-on:countiesInput="setCounties($event)"
        v-on:modeInput="setMode($event)"
        v-on:freeFlow="$emit('freeFlow', $event)"
        v-on:avgTravel="$emit('bgSummaryLayerOn', $event);"
        v-on:demographicInput="setDemographic($event)"
        v-on:dotDensDemographicInput="setdotDensDemographic($event)"
        v-on:jobInput="setJobs($event)"
        v-on:allowZoom="setAllowZoom($event)"
        v-on:expandedInput="amenitySelectExpanded = $event"
    />
    <div class="map-overlay" id="legend">
      <div v-if="!jobColumn" class="legend-heading no-print">Showing <strong>{{ modeNames[data_table].name }}</strong>
        Access to <strong>{{
            amenityNames[mappedAmenity]
          }}</strong>
        for:
      </div>
      <div v-if="jobColumn" class="legend-heading no-print">Showing <strong>{{
          jobColumnNames[jobColumn]
        }}</strong> by <strong>{{ modeNames[data_table].name }}</strong> Mode
      </div>
      <mcdSelect
          v-if="boundaryNames && boundaryNames.length"
          v-bind:counties="counties"
          v-bind:mcd="mcd"
          v-bind:boundaryNames="boundaryNames"
          v-on:mcdInput="setMcd($event)"
          v-on:countiesInput="setCounties($event)"
      />

      <div v-if="bgSummaryLayerOn">
        <div v-for="(bi) in bivariateLayerLegend"
             v-bind:key="bi.threshold" style="display: grid; text-align: left; grid-template-columns: 40px 250px;">
          <font-awesome-icon
              icon="square"
              class="bi-icon"
              style="grid-column: 1; opacity: .8;"
              v-bind:style="{color: bi.color}"/>
          <span style="font-size: small; grid-column: 2; margin-left: 6px;" v-html="bi.threshold"></span>
        </div>
      </div>

      <table v-if="!(jobColumn || bgSummaryLayerOn)">
        <tr>
          <th>Minutes</th>
          <th v-if="demographicColumn">
            <demographicSelect
                v-bind:demographicColumns="demographicColumns"
                v-bind:demographicColumn="demographicColumn"
                v-bind:boundaryNames="boundaryNames"
                v-on:demographicInput="setDemographic($event)"
            />
          </th>
        </tr>
        <tr
            v-for="leg in legend"
            v-bind:key="leg.name"
        >
          <th
              v-bind:style="{background: colorMap[leg.name]}"
              style="opacity: .9">{{ leg.value }}
          </th>
          <td v-if="demographicColumn">{{ format(leg.demographic_count) }}
            ({{ filterPercent(leg.demographic_count / legendDataTotal) }})
          </td>
        </tr>
      </table>
      <div v-if="demographicColumn && maxofpoints" class="demographic-legend">
        <div><strong>{{ demographicColumnNames[demographicColumn] }}:</strong></div>
        <span v-for="(cir, index) in demographicCircleSizes"
              v-bind:key="cir">
                    <font-awesome-icon
                        icon="circle"
                        class="demo-icon"
                        v-bind:style="{fontSize: cir}"/>
                <span
                    class="demo-text"> {{
                    Math.floor(percentile_values[index] || 0).toLocaleString(undefined, {maximumFractionDigits: 0})
                  }} </span>
                </span>
        <span><font-awesome-icon
            icon="circle"
            class="demo-icon"
            v-bind:style="{fontSize: '37px'}"/> </span>
        <span class="demo-text" v-show="maxofpoints"> {{ Math.floor(maxofpoints).toLocaleString() }}</span>


      </div>
      <TimeSlider class="no-print" v-if="(demographicColumn && !((jobColumn) || (bgSummaryLayerOn)))" :legend="legend"
                  :data_table="data_table"
                  :modeNames="modeNames" :demographicColumn="demographicColumn"
                  :demographicColumnNames="demographicColumnNames"
                  @time-slider-input="$emit('time-slider-input', $event)"></TimeSlider>
      <div v-if="jobColumn && maxofjobpoints" class="demographic-legend">
        <div v-if="!jobColumn.includes('gravity')"><strong>Total Number of Jobs:</strong></div>
        <div v-if="jobColumn.includes('gravity')"><strong>Access to Jobs:</strong></div>
        <div v-if="jobColumn.includes('gravity')">
          <span>Low</span>
          <span v-for="(cir, index) in legendMarks"
                v-bind:key="cir">
                    <font-awesome-icon
                        icon="square"
                        class="job-icon"
                        v-bind:style="{color: jobColorMap.toReversed()[index]}"/>
                    <span style="margin-right: 2px"></span>
                </span>
          <span>High</span>
        </div>
        <div v-if="!jobColumn.includes('gravity')">
                  <span v-for="(cir, index) in legendMarks"
                        v-bind:key="cir">
                    <font-awesome-icon
                        icon="square"
                        class="job-icon"
                        v-bind:style="{color: jobColorMap.toReversed()[index]}"/>
                <span
                    class="demo-text"> {{
                    Math.floor(legendMarks[index] || 0).toLocaleString(undefined, {maximumFractionDigits: 0})
                  }} </span>
                </span>
        </div>

      </div>
      <div
          v-if="dotDensDemographicColumn && !(dotDensDemographicColumn === 'demographic_emphasis_area' || dotDensDemographicColumn === 'affordable_housing')">
        {{ dotDensDemographicColumnNames[dotDensDemographicColumn] }}
      </div>
      <span
          v-if="dotDensDemographicColumn && !(dotDensDemographicColumn === 'demographic_emphasis_area' || dotDensDemographicColumn === 'affordable_housing')"><font-awesome-icon
          style="color: #1a0057; width: 4px" icon="circle"/></span><span
        v-if="dotDensDemographicColumn && !(dotDensDemographicColumn === 'demographic_emphasis_area' || dotDensDemographicColumn === 'affordable_housing')"> {{
        dot_ratios
      }} {{ this.dot_units }}/dot</span>

    </div>

    <div class="loader" v-if="!amenitySelectExpanded && !loaded">
      <div class="CDB-LoaderIcon CDB-LoaderIcon--big">
        <svg class="CDB-LoaderIcon-spinner" viewBox="0 0 50 50">
          <circle class="CDB-LoaderIcon-path" cx="25" cy="25" r="20" fill="none"/>
        </svg>
      </div>
      <div class="calc">Processing travel time... <br> Zoom in for higher accuracy</div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import * as turf from 'turf'
import * as d3 from 'd3'
import AmenitySelect from "./AmenitySelect"
import mcdSelect from "./mcdSelect"
import demographicSelect from "./demographicSelect"
import MapboxLayer from "./MapboxLayer";
import MapboxLayer_FromTiles from "./MapboxLayer_FromTiles";
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import LayerList from "@/components/LayerList.vue";
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import KDBush from 'kdbush';
import geokdbush from 'geokdbush';
import isobands from "../isobands";
import debounce from 'lodash.debounce'
import {featureCollection} from "turf/src";
import TimeSlider from "@/components/TimeSlider.vue";

export default {
  name: "mainmap",
  components: {TimeSlider, AmenitySelect, MapboxLayer, MapboxLayer_FromTiles, mcdSelect, demographicSelect, LayerList},
  props: [
    'data_table',
    'data_tables',
    'mappedAmenity',
    'destLookup',
    'amenities',
    'counties',
    'mcd',
    'amenityNames',
    'modeNames',
    'demographicColumns',
    'demographicColumn',
    'demographicColumnNames',
    'dotDensDemographicColumns',
    'dotDensDemographicColumn',
    'dotDensDemographicColumnNames',
    'dotDensDemographicURLs',
    'bivariateLayersLookup',
    'jobColumns',
    'jobColumn',
    'jobColumnNames',
    'startExpanded',
    'printOnLoad',
    'timeFilter',
    'bgSummaryLayerOn',
    'freeFlow',
  ],
  data: function () {
    return {
      regionBoundarys: undefined,
      transitServiceArea: undefined,
      schoolDistrictBoundaries: undefined,
      zoomControl: new mapboxgl.NavigationControl(),
      boundaryNames: [],
      loaded: false,
      amenitySelectExpanded: this.startExpanded,
      maxofpoints: 0,
      maxofjobpoints: 0,
      zoom: 0,
      zoomEnabled: false,
      percentile_values: [],
      job_percentile_values: [],
      demographicCircleSizes: ['10px', '14px', '27px'],
      jobCircleSizes: ['10px', '14px', '27px'],
      transitLayerOn: false,
      landUseContextLayerOn: false,
      schoolDistrictLayerOn: false,
      currentPopup: new mapboxgl.Popup(),
      isobands_poly: {
        type: 'FeatureCollection',
        features: []
      },
      voronoi_poly: {
        type: 'FeatureCollection',
        features: []
      },
      isoPoints: {
        type: 'FeatureCollection',
        features: []
      },
      jobPoints: {
        type: 'FeatureCollection',
        features: []
      },
      demographicPoints: {
        type: 'FeatureCollection',
        features: []
      },
      legendDataset: {},
    }
  },
  computed: {
    map: function () {
      mapboxgl.accessToken = 'pk.eyJ1Ijoic2VtY29nIiwiYSI6ImlvNEFUcmMifQ.VmDLZZG7l6syscFzkBs5eg'
      return new mapboxgl.Map({
        container: this.$refs.mapgl,
        style: 'mapbox://styles/semcog/cjd0pvt4a1r5n2rmkg7di38sm',
        center: [-83.2437186609522, 42.454430721108764],
        zoom: 8.3,
        minZoom: 8.05,
        maxZoom: 13.95,
        maxBounds: [-87, 40, -79, 44],
        scrollZoom: true,
      }).addControl(new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        bbox: [-85.11, 41.55, -81.46, 43.32],
        collapsed: true,
        marker: true,
        flyTo: true,
        mapboxgl: mapboxgl,
      }).on('result', (r) => {
        this.missingFeaturePopup(r)
      }), 'bottom-left')
    },
    boundary: function () {
      if (!this.regionBoundarys) {
        return this.regionBoundarys
      }
      if (this.amenitySelectExpanded) {
        return this.regionBoundarys[0];
      }
      if (Array.isArray(this.mcd)) {
        if (this.mcd.length === this.boundaryNames.length) {
          return this.regionBoundarys[0];
        }
        return turf.union(turf.featureCollection(this.mcd.map(m => this.regionBoundarys[m] || this.regionBoundarys[0])))
      }
      return turf.rewind(this.regionBoundarys[this.mcd || 0] || this.regionBoundarys[0])
    },
    legendData: function () {
      let data = this.demographicColumn
          && this.legendDataset[this.data_table]
          && this.legendDataset[this.data_table][this.mappedAmenity.toLowerCase()]
          && this.legendDataset[this.data_table][this.mappedAmenity.toLowerCase()][this.demographicColumn];
      if (!data) {
        return []
      }
      if (Array.isArray(this.mcd)) {
        let raw = this.mcd.map(m => data[m]);
        return data[0].map((_, i) =>
            raw.map(row =>
                (row && row[i]) || 0
            ).reduce(
                ((total, num) => total + num)
                , 0)
        )
      }
      return (data[this.mcd] || 0) || []
    },
    legendDataTotal: function () {
      let sum = 0
      this.legendData.forEach(d => sum += d)
      return sum
    },
    legend: function () {
      let breakPoints = this.modeNames[this.data_table].breakPoints;
      let visibleLayer = [];
      for (let feat of this.isobands_poly.features) {
        visibleLayer[feat.properties.name] = true;
      }
      return breakPoints.map((start, i) => {
        return {
          name: i,
          value: breakPoints[i + 1] ? (start + '-' + breakPoints[i + 1]) : '>' + start,
          demographic_count: (this.legendData || [])[i] || 0,
          visible: visibleLayer[i],
        }
      }).filter(d => d.visible || d.demographic_count)
    },
    colorLegendData: function () {
      let out = []
      for (let i = 0; i <= this.job_percentile_values.length; i += 1) {
        out.push(
            {
              key: [
                this.job_percentile_values[i - 1] || -Infinity,
                this.job_percentile_values[i] || Infinity,
              ],
              value: this.jobColorMap[i],
            }
        )
      }
      return out
    },
    legendMarks: function () {
      let d = [].concat(this.colorLegendData.map(legend => legend.key[1]).filter(isFinite)).concat([this.maxofjobpoints])
      return Array.from(new Set(d))
    },
    colorMap: function () {
      return this.modeNames[this.data_table].colorMap
    },
    jobColorMap: function () {
      let gravity = [
        '#5c53a5',
        '#a059a0',
        '#ce6693',
        '#eb7f86',
        '#f8a07e',
        '#fffdf8',
      ]
      let cumulative = [
        '#541f3f',
        '#813753',
        '#a65461',
        '#c1766f',
        '#fbf5dc',
        '#fffdf8',
      ]
      if (this.jobColumn === 'jobs_gravity_90min') {
        return gravity
      } else {
        return cumulative
      }
    },
    bivariateLayerLegend: function () {

      let breakpoint = this.bivariateLayersLookup[this.data_table].break
      let mode = this.modeNames[this.data_table].name
      let cutoff = this.modeNames[this.data_table].breakPoints.at(-1)

      return [{
        color: '#79379C',
        threshold: `<strong>High</strong> Transportation Access Focus Population, <strong>${breakpoint} to ${cutoff}min</strong> Avg ${mode} Time`
      },
        {
          color: '#FFD007',
          threshold: `<strong>Low</strong> Transportation Access Focus Population, <strong>${breakpoint} to ${cutoff}min</strong> Avg ${mode} Time`
        },
        {
          color: '#66A7FF',
          threshold: `<strong>High</strong> Transportation Access Focus Population, <strong><${breakpoint}min</strong> Avg ${mode} Time`
        },
        {
          color: '#BEE8FF',
          threshold: `<strong>Low</strong> Transportation Access Focus Population, <strong><${breakpoint}min</strong> Avg ${mode} Time`
        },
        {
          color: '#c6c6c6',
          threshold: `<strong>High</strong> Transportation Access Focus Population, <strong>>${cutoff}min</strong> Avg ${mode} Time`
        },
        {
          color: '#ffffff',
          threshold: `<strong>Low</strong> Transportation Access Focus Population, <strong>>${cutoff}min</strong> Avg ${mode} Time`
        }
      ]
    },
    tilesetLayer: function () {
      return [
        {
          type: 'circle',
          paint: {
            'circle-opacity': 0,
            'circle-color': '#b730fa',
            'circle-radius': 3,
            'circle-stroke-width': 0,
          },
          'source-layer': this.modeNames[this.data_table].sourceName
        }
      ];
    },
    jobLayer: function () {
      if (!this.jobColumn) {
        return []
      }
      let color = '#fff'
      let color_map = this.jobColorMap.toReversed()
      if (this.job_percentile_values.length) {
        color = ['step', ['get', this.jobColumn]]
        for (let i = 0; i < this.job_percentile_values.length; i += 1) {
          color.push(color_map[i], this.job_percentile_values[i])
        }
        color.push(color_map[this.job_percentile_values.length])
      }
      return [
        {
          type: 'circle',
          paint: {
            'circle-radius': 1,
            'circle-opacity': 0,
            'circle-color': color,
            'circle-stroke-width': 0,
          }
        }
      ]
    },
    voronoiLayer: function () {
      if (!this.jobColumn) {
        return []
      }
      let color = '#ffffff'
      let color_map = this.jobColorMap.toReversed()
      if (this.job_percentile_values.length) {
        color = ['step', ['get', this.jobColumn]]
        for (let i = 0; i < this.job_percentile_values.length; i += 1) {
          color.push(color_map[i], this.job_percentile_values[i])
        }
        color.push(color_map[this.job_percentile_values.length])
      }
      return [
        {
          type: 'fill',
          paint: {
            'fill-color': color,

          }
        },
      ]
    },
    demographicLayer: function () {
      if (!this.demographicColumn) {
        return []
      }
      let radius = 0;
      if (this.maxofpoints > 10 && this.percentile_values.length > 0 && !this.amenitySelectExpanded) {
        radius = ['step', ['get', this.demographicColumn],
          5, this.percentile_values[0],
          8, this.percentile_values[1],
          15, this.percentile_values[2],
          20
        ]
      }

      return [
        {
          type: 'circle',
          layout: {
            visibility: this.bgSummaryLayerOn ? 'none' : 'visible',
          },
          filter: ['all', ['>', ['get', this.demographicColumn], 10], ['>', ['/', ['get', this.mappedAmenity], ['get', 'point_count']], 2]],
          paint: {
            'circle-color': '#e5b061',
            'circle-opacity': .9,
            //'circle-blur': .6,
            'circle-radius': radius,
          },
        },
        {
          type: 'symbol',
          filter: ['all', ['>', ['get', this.demographicColumn], 10], ['>', ['/', ['get', this.mappedAmenity], ['get', 'point_count']], 2]],
          layout: {
            visibility: this.bgSummaryLayerOn ? 'none' : 'visible',
            'text-field': ['get', this.demographicColumn],
            'text-size': [
              'interpolate',
              ['linear'],
              ['zoom'],
              6,
              0,
              9,
              10,
              22,
              12],
            "text-font": ["Arial Regular"],
          },
          paint: {
            //'text-opacity': text_opacity,
            'text-color': '#000000',
            'text-halo-width': 1,
            'text-halo-color': '#fff',
            //'text-halo-blur': 1.5,
          },
        },
      ];
    },
    demographicClusterSettings: function () {
      let out = {
        clusterRadius: 40,
        clusterProperties: {}
      };
      this.demographicColumns.forEach((d) => {
        if (d.value) {
          out.clusterProperties[d.value] = ['+', ['get', d.value]];
        }
      });
      out.clusterProperties[this.mappedAmenity] = ['+', ['get', this.mappedAmenity]]
      return out;
    },
    dot_dens_demographics: function () {

      let dea_layer = [
        {
          type: 'fill',
          filter: [">", ['get', this.dotDensDemographicColumn], 0],
          // layout: {
          //   visibility: this.bgSummaryLayerOn ? 'visible' : 'none',
          // },
          paint: {
            'fill-color': '#8e8e8e',
            'fill-opacity': 0.5,
          },
          'source-layer': 'block_groups_travel_time_summary'
        },
        {
          type: 'line',
          filter: [">", ['get', this.dotDensDemographicColumn], 0],
          // layout: {
          //   visibility: this.bgSummaryLayerOn ? 'visible' : 'none',
          // },
          paint: {
            'line-width': 2,
            'line-color': '#8e8e8e',
          },
          'source-layer': 'block_groups_travel_time_summary'
        },
      ]


      if (!this.dotDensDemographicColumn) {
        return []
      }
      let radius = [
        'interpolate',
        ['linear'],
        ['zoom'],
        7,
        .8,
        9,
        1,
        12,
        1.4,
        14,
        0]

      let opacity = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        0.6,
        11,
        1,
        14,
        0]
      let dot_dens = [
        {
          type: 'circle',
          //filter,
          // layout: {
          //   visibility: this.selectedLayers.overlays === 'dot_dens_race_circle_0' ? 'visible' : 'none',
          // },
          paint: {
            'circle-opacity': opacity,
            'circle-color': '#1a0057',
            'circle-radius': radius,
          },
          'source-layer': this.dotDensDemographicColumn + '_dot_dens',
        },
      ]

      //let filter = ['in', ['get','ind'], ["literal", ['hhs_single_parent']]]

      if (this.dotDensDemographicColumn === 'demographic_emphasis_area' || this.dotDensDemographicColumn === 'affordable_housing') {
        return dea_layer
      } else {
        return dot_dens
      }
    },
    dot_units: function () {
      if (this.dotDensDemographicColumn.includes('pop') && parseInt(this.dot_ratios) > 1) {
        return 'persons'
      } else if ((this.dotDensDemographicColumn.includes('hh') || this.dotDensDemographicColumn.includes('house')) && parseInt(this.dot_ratios) > 1) {
        return 'households'
      }
      if (this.dotDensDemographicColumn.includes('pop') && parseInt(this.dot_ratios) <= 1) {
        return 'person'
      } else if ((this.dotDensDemographicColumn.includes('hh') || this.dotDensDemographicColumn.includes('house')) && parseInt(this.dot_ratios) <= 1) {
        return 'household'
      }
      return 'N/A'
    },
    dot_ratios: function () {
      let ratios = {
        'hhs_in_poverty': {
          5: 7.593642663452847,
          6: 6.108262039213786,
          7: 6.142801433132663,
          8: 5.10425906798712,
          9: 3.541530246609991,
          10: 2.1566904270885923,
          11: 0.9995518469458171,
        },
        'hhs_single_parent': {
          5: 7.593719806763285,
          6: 5.0624798711755234,
          7: 4.753011137428817,
          8: 3.9100368973093986,
          9: 2.6531077260643907,
          10: 1.538903347392982,
          11: 0.9994383629870611,
        },
        'hhs_female_head': {
          5: 7.593687390192029,
          6: 6.146603128664455,
          7: 6.140781176001396,
          8: 5.069385571422692,
          9: 3.396644193517255,
          10: 1.9363058326063207,
          11: 0.9995172041658383,
        },

        'hhs_snap': {
          5: 7.593599747773311,
          6: 6.874063503389226,
          7: 6.995183580608466,
          8: 5.826311333306455,
          9: 4.1058643027616775,
          10: 2.5128459271238164,
          11: 1.1611425816560204,
        },
        'hhs_limited_english': {
          5: 7.593647479868774,
          6: 5.0624316532458495,
          7: 3.3748426005699517,
          8: 2.2487524839920514,
          9: 1.4991021225234773,
          10: 0.9995289222132805,
          11: 0.9994504523954387,
        },
        'pop_disability': {
          5: 17.282708796443316,
          6: 17.898459767594826,
          7: 17.76843966807237,
          8: 12.759187261893132,
          9: 7.018883133073963,
          10: 3.4577129968868463,
          11: 1.4467503007237308,
        },
        'pop_poverty': {
          5: 15.618413520173233,
          6: 16.96269564512205,
          7: 16.929745042492918,
          8: 12.437830766486293,
          9: 7.713500755983332,
          10: 4.239212825033947,
          11: 1.8981073404458049,
        },
        'pop_edu_lessthan_hs': {
          5: 7.593806188444301,
          6: 6.852852852852853,
          7: 6.915489515616599,
          8: 5.6345679012345675,
          9: 3.766679090570257,
          10: 2.1209768090305365,
          11: 0.9995690416475326,
        },
        'pop_edu_some_college': {
          5: 51.42424827053992,
          6: 52.708438091471514,
          7: 47.09303382130237,
          8: 35.73352484918127,
          9: 17.097121807735657,
          10: 7.8583925435179065,
          11: 3.0361294038965667,
        },
        'pop_edu_bachlor_or_higher': {
          5: 32.06822827022375,
          6: 33.13488204073693,
          7: 33.18375354817165,
          8: 24.37948518738116,
          9: 11.383858248923596,
          10: 4.960211815406118,
          11: 2.1664422872528872,
        },
        'population': {
          5: 137.30497224924187,
          6: 125.5489052240563,
          7: 126.39868317092441,
          8: 95.19513646461441,
          9: 42.61701712012503,
          10: 18.659878228007557,
          11: 6.972638901197565
        },
        'households': {
          5: 53.42123448599939,
          6: 54.92270092076844,
          7: 48.78505616559384,
          8: 37.77599687255668,
          9: 17.924670048878212,
          10: 8.083705599892921,
          11: 3.018864977248622,
        },
        'affordable_housing': {
          5: 37.50863410080896,
          6: 39.68379327371482,
          7: 39.62075482033304,
          8: 25.899330421082784,
          9: 15.041986836222225,
          10: 7.988695916282409,
          11: 3.043415970311277,
        },
        'demographic_emphasis_area': {
          5: 22.890420883425026,
          6: 25.976234520322695,
          7: 24.18541287915032,
          8: 20.404581231079717,
          9: 12.824361348588244,
          10: 6.964236759266275,
          11: 2.6795265594729183,
        }
      }
      if (this.dotDensDemographicColumn && ratios[this.dotDensDemographicColumn][Math.floor(this.zoom)] && (this.zoom > 4)) {
        return this.format1dec(ratios[this.dotDensDemographicColumn][Math.floor(this.zoom)])
      } else {
        return '1'
      }
    },
    isoPointsLayer: function () {
      let breakPoints = this.modeNames[this.data_table].breakPoints;
      return [
        {
          type: 'circle',
          paint: {
            'circle-radius': 3,
            'circle-color': [
              'interpolate',
              ['linear'],
              ['get', 'value'],
              breakPoints[0], this.colorMap[0],
              breakPoints[1], this.colorMap[1],
              breakPoints[2], this.colorMap[2],
              breakPoints[3], this.colorMap[3],
              breakPoints[4], this.colorMap[4],
              //breakPoints[5], this.colorMap[5],
            ],
            'circle-stroke-width': 0.5,
          }
        }
      ]
    },
    boundaryLayer: function () {
      return [
        {
          type: 'line',
          paint: {
            'line-width': 4,
            'line-opacity': 0.9,
            'line-color': 'black',
            'line-blur': 3
          }
        }
      ]
    },
    isobandlinesLayer: function () {
      return [
        {
          type: 'line',
          layout: {
            'visibility': !(this.jobColumn || this.bgSummaryLayerOn) ? 'visible' : 'none',
          },
          paint: {
            'line-width': .5,
            'line-color': '#bcbcbc',
          }
        }
      ]
    },
    isobandsLayer: function () {
      return [
        {
          type: 'fill-extrusion',
          layout: {
            'visibility': !(this.jobColumn || this.bgSummaryLayerOn) ? 'visible' : 'none',
          },
          paint: {
            'fill-extrusion-color': [
              'interpolate',
              ['linear'],
              ['get', 'name'],
              0, this.colorMap[0],
              1, this.colorMap[1],
              2, this.colorMap[2],
              3, this.colorMap[3],
              4, this.colorMap[4],
            ],
            'fill-extrusion-opacity': 0.9,
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['get', 'name'],
              0, 45,
              1, 25,
              2, 10,
              3, 2,
              4, 0,],
          }
        },
      ]
    },
    destLayer: function () {

      let bigger_sizes = ['hospitals'
        , 'passenger_train_stations'
        , 'passenger_airports'
        , 'community_colleges'
        , 'american_job_centers']

      let smaller_sizes = ['fixed_route_bus', 'parks_local_school', 'childcare', 'pharmacies']

      let size = ['interpolate', ['linear'], ['zoom'],
        7, 0.2,
        9, 0.7,
        12, 1]

      if (bigger_sizes.includes(this.mappedAmenity)) {
        size = ['interpolate', ['linear'], ['zoom'],
          7, .8,
          9, 1,
          12, 1.3]
      }

      if (smaller_sizes.includes(this.mappedAmenity)) {
        size = ['interpolate', ['linear'], ['zoom'],
          6, 0.1,
          9, 0.3,
          12, 0.6]
      }

      let out = {
        type: 'symbol',
        layout: {
          'icon-image': 'park-marker-15-red-white-stroke',
          'visibility': !this.jobColumn ? 'visible' : 'none',
          'icon-allow-overlap': true,
          'icon-pitch-alignment': 'viewport',
          'icon-anchor': 'bottom',
          'icon-size': size,
        },
        'source-layer': 'all_core_services'
      };
      if (this.mappedAmenity && this.destLookup[this.mappedAmenity]) {
        out.filter = ['in', this.destLookup[this.mappedAmenity], ["get", "MERGE_SRC"]]
      }
      return [out]
    },
    landUseContextLayer: function () {
      return [
        {
          type: 'line',
          layout: {
            'visibility': this.landUseContextLayerOn ? 'visible' : 'none',
          },
          paint: {
            'line-width': 2,
            'line-opacity': 0.9,
            'line-color': [
              'match',
              ['get', '_symbol'],
              0,
              '#FF0000',
              1,
              '#FFAA00',
              2,
              '#00C5FF',
              3,
              '#FF00C5',
              4,
              '#4CE600',
              /* other */ '#ccc'
            ],
          },
          'source-layer': 'Land Use Context'
        }
      ]
    },
    transitProvidersLayer: function () {
      let width = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        .7,
        12,
        2,
        22,
        6]
      return [
        {
          type: 'line',
          layout: {
            'visibility': this.transitLayerOn ? 'visible' : 'none',
          },
          paint: {
            'line-width': width,
            'line-opacity': 0.8,
            'line-color': [
              'match',
              ['get', 'Label'],
              'Ann Arbor Area Transportation Authority (AAATA)',
              '#F58231',
              'University of Michigan Transit',
              '#09284c',
              'Blue Water Area Transit (BWAT)',
              '#4363D8',
              'Detroit Department of Transportation (DDOT)',
              '#FFD007',
              'Detroit People Mover',
              '#55FF00',
              'Lake Erie Transit',
              '#911EB4',
              'Michigan Flyer',
              '#FF0000',
              'Regional Transit Authority Detroit Air Xpress (RTA DAX)',
              '#4CE600',
              'Regional Transit Authority Detroit Ann Arbor Express (RTA D2A2)',
              '#730000',
              'Regional Transit Authority QLINE (RTA)',
              '#FF00C5',
              'Suburban Mobility Authority for Regional Transportation (SMART)',
              '#70A800',
              /* other */ '#ccc'
            ],
          },
          'source-layer': 'transit_providers_for_web'
        }
      ]
    },
    schoolDistrictBoundaryLayer: function () {
      let width = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        1,
        22,
        5]
      let text_size = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        0,
        11,
        14,
        22,
        16]
      let text_opacity = [
        'interpolate',
        ['linear'],
        ['zoom'],
        8,
        0.7,
        14,
        1]
      return [
        {
          type: 'line',
          filter: ["==", "geotype", 'SchoolDistrict'],
          layout: {
            visibility: this.schoolDistrictLayerOn ? 'visible' : 'none',
          },
          paint: {
            'line-width': width,
            'line-opacity': 0.9,
            'line-color': '#2e2e2e',
          }
        },
        {
          type: 'symbol',
          filter: ["==", "geotype", 'SchoolDistrict'],
          layout: {
            visibility: this.schoolDistrictLayerOn ? 'visible' : 'none',
            'text-field': ['get', 'name'],
            'text-size': text_size,
            "text-font": ["Arial Regular"],
          },
          paint: {
            'text-opacity': text_opacity,
            'text-color': '#000000',
            'text-halo-width': 1,
            'text-halo-color': 'white',
            'text-halo-blur': 1.5,
          },
        },
      ]
    },
    bgTravelTimeLayer: function () {
      let cutoff = this.modeNames[this.data_table].breakPoints.at(-1)
      return [
        {
          type: 'fill',
          filter: ['all', [">=", ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']], 0], ['==', ['get', 'demographic_emphasis_area'], 1]],
          layout: {
            visibility: this.bgSummaryLayerOn ? 'visible' : 'none',
          },
          paint: {
            'fill-color': [
              'step',
              ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']],
              '#66A7FF',
              this.bivariateLayersLookup[this.data_table].break, '#66A7FF',
              this.bivariateLayersLookup[this.data_table].break + .01, '#79379C',
              cutoff, '#c6c6c6'
            ],
            'fill-opacity': .8
          },
          'source-layer': this.bivariateLayersLookup[this.data_table].name
        },
        {
          type: 'fill',
          filter: ['all', [">=", ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']], 0], ['==', ['get', 'demographic_emphasis_area'], 0]],
          layout: {
            visibility: this.bgSummaryLayerOn ? 'visible' : 'none',
          },
          paint: {
            'fill-color': [
              'step',
              ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']],
              '#BEE8FF',
              this.bivariateLayersLookup[this.data_table].break, '#BEE8FF',
              this.bivariateLayersLookup[this.data_table].break + .01, '#FFD007',
              cutoff, '#ffffff'
            ],
            'fill-opacity': .8
          },
          'source-layer': this.bivariateLayersLookup[this.data_table].name
        },
        {
          type: 'line',
          filter: [">=", ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']], 0],
          layout: {
            visibility: this.bgSummaryLayerOn ? 'visible' : 'none',
          },
          paint: {
            'line-width': 1,
            'line-opacity': 0.3,
            'line-color': 'white',
          },
          'source-layer': this.bivariateLayersLookup[this.data_table].name
        },
        {
          type: 'symbol',
          filter: ['all', [">=", ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']], 0], ["<=", ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']], cutoff]],
          layout: {
            visibility: this.bgSummaryLayerOn ? 'visible' : 'none',
            'text-field': [
              "concat",
              ['round', ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']]],
              "min"
            ],
            'text-size': [
              'interpolate',
              ['linear'],
              ['/', ['get', this.mappedAmenity], ['get', 'Join_Count']],
              5, 5,
              10, 10,
              15, 12,
              30, 16,
              60, 28,
            ],
            "text-font": ["Arial Regular"],
          },
          paint: {
            //'text-opacity': text_opacity,
            'text-color': '#000000',
            'text-halo-width': 1,
            'text-halo-color': '#fff',
            //'text-halo-blur': 1.5,
          },
          'source-layer': this.bivariateLayersLookup[this.data_table].name
        },
      ]
    },
  },
  methods: {
    titleCase: function (str) {
      return str.toLowerCase().split(' ').map(function (word) {
        return word.replace(word[0], word[0].toUpperCase());
      }).join(' ');
    },
    format: d3.format(','),
    format1dec: d3.format('.1f'),
    format2dec: d3.format('.2f'),
    format0deccom: d3.format(',.0f'),
    filterPercent: function (percent) {
      if (!isNaN(percent) && isFinite(percent)) {
        percent = d3.format('.1%')(percent)
      } else {
        percent = 'N/A'
      }
      return percent
    },
    bivariatePopup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      let dea = feature.properties.demographic_emphasis_area ? 'high' : 'low'
      let high_low = (feature.properties[this.mappedAmenity] / feature.properties.Join_Count > this.bivariateLayersLookup[this.data_table].break) ? 'high' : 'low'
      let html = ``
      if (feature.properties) {
        html = `<div>This block group has <strong>${dea}</strong> Transportation Access Focus Population and <strong>${high_low}</strong> ${this.modeNames[this.data_table].name} travel time to ${this.amenityNames[this.mappedAmenity]}. </div>`
      }
      this.currentPopup.setLngLat([featureEvent.lngLat.lng, featureEvent.lngLat.lat]).setHTML(html).addTo(this.map);
    },
    transitProviderPopup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      let html = ``
      if (feature.properties.Label) {
        html = `<div class="popup"> Transit Provider:</div> <div>${feature.properties.Label}</div>`
      }
      this.currentPopup.setLngLat([featureEvent.lngLat.lng, featureEvent.lngLat.lat]).setHTML(html).addTo(this.map);
    },
    landUseContextPopup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      let html = ``
      if (feature.properties._symbol >= 0) {
        html = `<div class="popup"> Land Use Context:</div> <div>${this.getLandUseContext(feature.properties._symbol)}</div>`
      }
      this.currentPopup.setLngLat([featureEvent.lngLat.lng, featureEvent.lngLat.lat]).setHTML(html).addTo(this.map);
    },
    popup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }

      let html = `<div>${this.amenityNames[this.mappedAmenity]}</div>`

      if (feature.properties.name) {
        html = `<div class="popup"> Name: ${feature.properties.name}</div> <div>${this.amenityNames[this.mappedAmenity]}</div>`
      }
      html += `<div><a style="color: #136400; font-size: x-small" href="mailto:makari@semcog.org?subject=At ${featureEvent.lngLat.lng}, ${featureEvent.lngLat.lat}, ${this.amenityNames[this.mappedAmenity]}">Send SEMCOG Feedback</a></div>`

      this.currentPopup.setLngLat([featureEvent.lngLat.lng, featureEvent.lngLat.lat]).setHTML(html).addTo(this.map);
    },
    demographic_update_popup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      const html = `
                    <div>${this.demographicColumnNames[this.demographicColumn]}: ${feature.properties[this.demographicColumn].toLocaleString()}</div>
                    <div>Average ${this.modeNames[this.data_table].name} Time: ${this.format0deccom(feature.properties[this.mappedAmenity] / feature.properties['point_count'])} min</div>
                     `;
      this.currentPopup.setLngLat(feature.geometry.coordinates)
          .setHTML(html)
          .addTo(this.map);
    },
    job_update_popup: function (featureEvent) {
      const feature = featureEvent.features[0];
      if (!feature) {
        return;
      }
      const html = `
                    <div>${this.jobColumnNames[this.jobColumn]}: ${feature.properties[this.jobColumn].toLocaleString()}</div>
                     `;
      this.currentPopup.setLngLat(featureEvent.lngLat)
          .setHTML(html)
          .addTo(this.map);
    },
    missingFeaturePopup: function (result) {
      const feature = result.result.geometry;
      if (!feature) {
        return;
      }
      let html = `<div><a style="color: #136400; font-size: x-small" href="mailto:makari@semcog.org?subject=Missing ${this.amenityNames[this.mappedAmenity]} at ${feature.coordinates[0]}, ${feature.coordinates[1]}">Send SEMCOG a missing ${this.amenityNames[this.mappedAmenity]}</a></div>`
      this.currentPopup.setLngLat([feature.coordinates[0], feature.coordinates[1] + 0.0012]).setHTML(html).addTo(this.map);
    },
    getLandUseContext: function (code) {
      return {
        0: 'Main Street',
        1: 'Urban or Walkable Thoroughfare',
        2: 'Suburban Corridors',
        3: 'Small Town Hamlet or Village Commercial',
        4: 'Rural',
      }[code]
    },
    setAmenity: function (id) {
      this.$emit('set-amenity', id);
    },
    setMode: function (id) {
      this.$emit('set-mode', id);
    },
    setDemographic: function (id) {
      this.$emit('set-demographic', id);
    },
    setdotDensDemographic: function (id) {
      this.$emit('set-dot-dens-demographic', id);
    },
    setJobs: function (id) {
      this.$emit('set-jobs', id);
    },
    setMcd: function (id) {
      this.$emit('set-mcd', id)
    },
    setCounties: function (id) {
      this.$emit('set-counties', id)
    },
    setAllowZoom: function (allowZoom) {
      let zoomOptions = ['touchZoomRotate', 'doubleClickZoom', 'boxZoom', 'scrollZoom', 'keyboard'];
      if (allowZoom) {
        if (!this.map._controls.includes(this.zoomControl)) {
          this.map.addControl(this.zoomControl, 'top-right');
        }
        zoomOptions.forEach((z) => {
          this.map[z].enable()
        });
        this.zoomEnabled = true;
      } else {
        if (this.map._controls.includes(this.zoomControl)) {
          this.map.removeControl(this.zoomControl);
        }
        zoomOptions.forEach((z) => {
          this.map[z].disable()
        });
        this.zoomEnabled = false;
      }
    },
    fitToBoundary: function () {
      if (this.amenitySelectExpanded) {
        return;
      }
      let bbox = turf.bbox(turf.buffer(
          turf.bboxPolygon(turf.bbox(this.boundary)),
          0.5,
          {units: 'kilometers', steps: 4}
      ));
      let padding = {top: 1, bottom: 1, left: 1, right: 1};
      if (this.map.getCanvas().width > 1000) {
        padding = {top: 5, bottom: 5, left: 330, right: 330}
      }
      if (this.printOnLoad) {
        padding = {top: 1, bottom: 1, left: 5, right: 350}
      }
      if (this.mcd) {
        this.map.fitBounds(
            [[bbox[0], bbox[1]], [bbox[2], bbox[3]]],
            {padding: padding}
        )
      } else {
        // changing bounds from a small community to the region causes page crash
        this.map.jumpTo(this.map.cameraForBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]]))

      }
    },
    setJobStyle: function () {
      if (this.map.getLayer('jobPoints_circle_0') && !this.amenitySelectExpanded) {
        let job_features = this.map.queryRenderedFeatures({layers: ['jobPoints_circle_0']});
        let job_values = job_features.map(f => f.properties[this.jobColumn] || 1);
        this.maxofjobpoints = Math.max(0, ...job_values);
        let countofpoints = job_values.length || 0;
        if (countofpoints === 0 || this.maxofjobpoints === 0) {
          this.job_percentile_values = [];
          return
        }
        let sorted = job_values.sort(function (a, b) {
          return a - b
        });
        let percentiles = [.1, .3, .7, .9];
        this.job_percentile_values = percentiles.map((v) => {
          return sorted[Math.floor(v * countofpoints)] + v
        });
      }
    },
    setDemographicStyle: function () {
      if (this.map.getLayer('demographicPoints_circle_0') && !this.amenitySelectExpanded) {
        let demographic_features = this.map.queryRenderedFeatures({layers: ['demographicPoints_circle_0']});
        let demo_values = demographic_features.map(f => f.properties[this.demographicColumn] || 1);
        this.maxofpoints = Math.max(0, ...demo_values);
        let countofpoints = demo_values.length || 0;
        if (countofpoints === 0 || this.maxofpoints === 0) {
          this.percentile_values = [];
          return
        }
        let sorted = demo_values.sort(function (a, b) {
          return a - b
        });
        let percentiles = [.3, .7, .9];
        this.percentile_values = percentiles.map((v) => {
          return sorted[Math.floor(v * countofpoints)] + v
        });
      }
    },
    createVoronoi: function () {
      this.loaded = false
      let bounds = this.map.getBounds();
      let transitClippedArea = []
      let clippedBoundary = turf.intersect(this.boundary,
          turf.bboxPolygon([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()])
      );
      let transit_area = turf.featureCollection(this.transitServiceArea)
      if (this.data_table === 'transit_indicators_demographics_by_parcel') {
        transit_area.features.forEach(d => {
          let clipped_ta_part = turf.intersect(d, clippedBoundary)
          if (clipped_ta_part) {
            transitClippedArea.push(clipped_ta_part)
          }
        })
        clippedBoundary = turf.union(turf.featureCollection(transitClippedArea))
      }
      const options = {
        bbox: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] // Bounding box for the polygons
      };
      const voronoiPolygons = turf.voronoi(this.jobPoints, options);
      let voronoi_collection = [];
      voronoiPolygons.features.forEach((f) => {
        if (f.geometry.coordinates.length > 0) {
          let intersect_boundary = undefined
          let point_in_poly = undefined
          if (!turf.booleanWithin(f, clippedBoundary)) {
            intersect_boundary = turf.intersect(f, clippedBoundary);
          } else {
            intersect_boundary = f
          }
          if (intersect_boundary) {
            point_in_poly = turf.pointsWithinPolygon(this.jobPoints, intersect_boundary)
          }
          if (!point_in_poly) {
            return
          }
          if (point_in_poly.features[0]) {
            intersect_boundary.properties = point_in_poly.features[0].properties;
            voronoi_collection.push(intersect_boundary);
          }
        }
      });

      this.voronoi_poly = Object.freeze(turf.featureCollection(voronoi_collection))

    },
    interpolateTime: function () {
      if (this.amenitySelectExpanded || !this.boundary) {
        this.loaded = false;
        return
      }
      let features = this.map.getLayer('tileset_circle_0') && this.map.queryRenderedFeatures({layers: ['tileset_circle_0']});
      if (!features || features.length <= 1 || !this.boundary) {
        this.loaded = false;
        return
      }

      let bounds = this.map.getBounds();
      let clippedBoundary = turf.intersect(this.boundary,
          turf.bboxPolygon([bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()])
      );
      let demographicPoints = [];
      let jobPoints = [];
      let data = features.map(f => {
        let centroid = f.geometry.coordinates;
        if (turf.booleanPointInPolygon(centroid, clippedBoundary) && this.demographicColumn && f.properties[this.demographicColumn.toLowerCase()] > 0) {
          // show all the data
          if (this.timeFilter[0] === 0 && this.timeFilter[1] === 100) {
            demographicPoints.push(f);
            // show a range
          } else if (f.properties[this.mappedAmenity] >= this.timeFilter[0] &&
              f.properties[this.mappedAmenity] <= this.timeFilter[1]) {
            demographicPoints.push(f);
          }
        }
        if (turf.booleanPointInPolygon(centroid, clippedBoundary) && this.jobColumn && f.properties[this.jobColumn.toLowerCase()] > 0) {
          jobPoints.push(f);
        }
        return {
          centroid,
          value: f.properties[this.mappedAmenity.toLowerCase()],
        }
      });
      if (this.jobColumn) {
        this.map.setMaxZoom(13);
      } else {
        this.map.setMaxZoom(13.95)
      }
      this.maxofpoints = 0;
      this.maxofjobpoints = 0;
      this.demographicPoints = Object.freeze(turf.featureCollection(demographicPoints));
      this.jobPoints = Object.freeze(turf.featureCollection(jobPoints));
      if (this.jobColumn) {
        this.createVoronoi()
      }
      const index = new KDBush(data, p => p.centroid[0], p => p.centroid[1], 4, Float32Array);
      let zoom = Math.floor(this.map.getZoom());
      //the len_raw determines the distance between the grid points
      //(len_raw_at_minzoom - len_raw_at_maxzoom) / (minzoom - maxzoom) * (currentzoom - maxzoom) + len_raw_at_maxzoom
      let len_raw = ((.8 - 0.01) / (8 - 14)) * (zoom - 14) + 0.01;
      let len = parseFloat(len_raw.toPrecision(2));
      let degreesLen = turf.lengthToDegrees(len, 'kilometers');

      function roundToLen(num) {
        return Math.round(num / degreesLen) * degreesLen;
      }

      let clippedBoundaryBBox = turf.bbox(clippedBoundary);
      let west = roundToLen(clippedBoundaryBBox[0] - (2 * degreesLen));
      let south = roundToLen(clippedBoundaryBBox[1] - (2 * degreesLen));
      let east = roundToLen(clippedBoundaryBBox[2] + (2 * degreesLen));
      let north = roundToLen(clippedBoundaryBBox[3] + (2 * degreesLen));

      let grid = [];

      let interpolateSpeed = this.modeNames[this.data_table].interpolateSpeedMinPerKM || 0;
      let breakPoints = this.modeNames[this.data_table].breakPoints;
      let lastBreak = breakPoints[breakPoints.length - 1];
      for (let x = west; x <= east; x += degreesLen) {
        for (let y = south; y <= north; y += degreesLen) {
          let near = geokdbush.around(index, x, y, 3, lastBreak / interpolateSpeed);
          let vals = near.map(near => {
            let distKM = geokdbush.distance(x, y, near.centroid[0], near.centroid[1]);
            return near.value + interpolateSpeed * distKM;
          });
          grid.push(turf.point([x, y], {value: Math.min(...vals, lastBreak + 1)}));
        }
      }

      grid = turf.featureCollection(grid);

      // this.isoPoints = Object.freeze(grid);

      let access_isobands = isobands(grid, breakPoints, {
        zProperty: "value",
        breaksProperties: breakPoints.map((b, i) => {
          return {name: i}
        })
      });

      let isoband_collection = [];
      access_isobands.features.forEach((f) => {
        if (f.geometry.coordinates.length > 0) {
          let iso_intersect = turf.intersect(f, clippedBoundary);
          if (!iso_intersect) {
            return
          }
          iso_intersect.properties = f.properties;
          isoband_collection.push(iso_intersect);
        }
      });
      this.isobands_poly = Object.freeze(turf.featureCollection(isoband_collection));
      this.map.once('idle', () => {
        this.loaded = true;
        if (this.printOnLoad) {
          window.setTimeout(() => { // wait for loader to disappear
            window.focus();
            window.print();
            window.close();
          }, 400)
        }
      });
    }
  },
  mounted: function () {
    fetch('geojson/boundaries.json')
        .then(res => res.json())
        .then(res => {
          let regionBoundarys = {};
          let boundaryNames = [];
          res.features.forEach(f => {
            regionBoundarys[f.properties.mcd] = Object.freeze(f);
            if (f.properties.name) {
              boundaryNames.push(Object.freeze(f.properties));
            }
          });
          this.regionBoundarys = Object.freeze(regionBoundarys);
          this.boundaryNames = Object.freeze(boundaryNames);
        });

    fetch('geojson/transit_area.geojson')
        .then(res => res.json())
        .then(res => {
          this.transitServiceArea = Object.freeze(res.features);
        });
    fetch('geojson/boundaries_ce.geojson')
        .then(res => res.json())
        .then(data => {
          if (data.description) {
            let names = data.description.split(',')
            data.features.forEach(f => {
              let out = {}
              Object.keys(f.properties).forEach(p => {
                out[names[parseInt(p, 16)]] = f.properties[p]
              })
              f.properties = out
            })
          }
          let offset = data.features.find(f => f.properties.geoid === 8999)
          if (offset) {
            offset = offset.geometry && offset.geometry.coordinates
            if (offset.length && offset.length === 2) {
              let offset_x = offset[0]
              let offset_y = offset[1]
              turf.coordEach(data, pointCoords => {
                pointCoords[0] -= offset_x
                pointCoords[1] -= offset_y
              })
            }
          }
          this.schoolDistrictBoundaries = Object.freeze(featureCollection(data.features));
        });

    let update = debounce(() => {
      this.interpolateTime()
      this.setDemographicStyle();
    }, 500);
    this.setAllowZoom(true);
    this.map.once('idle', () => {
      this.map.on('sourcedata', (e) => {
        //console.log(this.map.getStyle().layers) //use this to figure out which layers are in the style
        if (e.sourceId === 'tileset') {
          update();
        }
        if (e.sourceId === 'demographicPoints') {
          this.setDemographicStyle();
        }
        if (e.sourceId === 'jobPoints') {
          this.setJobStyle();
        }
      });
      this.map.on('moveend', update);
      this.zoom = this.map.getZoom();
      this.map.on('zoomend', () => {
        this.zoom = this.map.getZoom();
      })
      update();
    });
  },
  watch: {
    boundary: function () {
      this.loaded = false;
      this.fitToBoundary();
    },
    mappedAmenity: function () {
      this.loaded = false;
      this.currentPopup.setLngLat([0, 0]);
      this.interpolateTime();
    },
    demographicColumn: function () {
      this.currentPopup.setLngLat([0, 0]);
      this.interpolateTime();
    },
    jobColumn: function () {
      this.currentPopup.setLngLat([0, 0]);
      this.interpolateTime();
      if (this.jobColumn) {
        this.$emit('bgSummaryLayerOn', false);
      }
    },
    timeFilter: debounce(function () {
      this.interpolateTime();
    }, 500),
    data_table: {
      immediate: true,
      handler: function () {
        this.loaded = false;
        this.currentPopup.setLngLat([0, 0]);
        if (!this.legendDataset[this.data_table]) {
          this.$set(this.legendDataset, this.data_table, {}); // place holder, so we dont have more then one fetch
          fetch('legend/' + this.data_table.replace('indicators_demographics_by_parcel', 'legend') + '.json')
              .then(res => res.json())
              .then(res => this.legendDataset[this.data_table] = Object.freeze(res))
        }
      }
    }
  }
}
</script>

<style scoped>
#map {
  grid-row: 2;
  position: relative;
}

/deep/ .mapboxgl-map {
  width: 100%;
  height: 100%;
}

div.selectors {
  top: 0;
  bottom: unset;
  height: fit-content;
  margin-top: 10px;
  right: auto;
  margin-left: 20px;
  padding: 8px;
  max-height: 90%;
  overflow: auto;
}

#legend {
  height: fit-content;
  margin-bottom: 20px;
  margin-right: 20px;
  padding: 8px;
  text-align: center;
  text-align: -webkit-center;
  text-align: -moz-center;
  color-adjust: exact;
  border: solid 1px #dddddd;
  box-shadow: #dddddd 0px 0px 0px 1px;
}

#legend table {
  border-collapse: collapse;
  align-self: center;
}

/deep/ #legend th select {
  border-radius: unset;
}

/deep/ #legend th select {
  border-color: rgba(243, 243, 243, 0);
}

/deep/ #legend th select:focus {
  outline: 0;
  border: unset;
}

/deep/ #legend th select {
  font-size: 1rem;
  font-weight: bold;
}

#legend td, #legend th {
  border: 1px solid #999;
  padding: 0.2rem;
}

#legend td {
  text-align: right;
}

.demographic-legend span {
  padding: 2px;
}

.demo-icon {
  border: 1px #c8c8c8 solid;
  border-radius: 100%;
  color: rgba(229, 176, 97, 0.9);
  vertical-align: middle;
}

.job-icon {
  border-radius: 100%;
  vertical-align: middle;
}

.bi-icon {
  border-radius: 100%;
  vertical-align: middle;
  font-size: 45px;
}


.demo-text {
  font-size: 0.756em;
}

.shift_layer_control {
  margin: 100px 0 0 0;
}

.map-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
  background: rgba(255, 255, 255, 1);
  font-family: Arial, sans-serif;
  border-radius: 3px;
}

.legend-heading {
  font-size: medium;
  max-width: 200px;
}

/deep/ .popup {
  color: #136400;
  font-size: large;
  font-weight: bolder;
  text-decoration: none;
}

/deep/ .popup-container {
  border-bottom: 2px solid black;
  margin-bottom: 10px;
}

/deep/ .amenityIcon-popup {
  height: 30px;
  width: 30px;
  padding: 2px;
  border: 2px solid black;
  border-radius: 7px;
  margin: 2px;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;
}

.calc {
  position: absolute;
  margin-top: 50px;
  margin-left: 10px;
  font-size: medium;
  color: rgba(255, 255, 255, .88);
  z-index: 5;
  text-align: center;
}

.loader {
  position: absolute;
  background: rgba(46, 60, 67, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 1;
  transition: opacity 300ms;
  pointer-events: none;
}

.CDB-LoaderIcon--big {
  width: 40px;
  height: 40px;
}

.CDB-LoaderIcon-spinner {
  width: 40px;
  height: 40px;
  animation: rotate 2s linear infinite;
}

.CDB-LoaderIcon-path {
  stroke: rgba(255, 255, 255, .88);
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
  stroke-width: 4px
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0
  }

  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35
  }

  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg)
  }
}

@media print {
  /deep/ .mapboxgl-ctrl-geocoder, .mapboxgl-ctrl-group {
    display: none;
  }

  /deep/ select {
    appearance: none;
    border: none;
    /* needed for Firefox: */
    overflow: hidden;
  }

  /deep/ .mapboxgl-control-container {
    display: none;
  }
}

@media print {
  .no-print {
    display: none !important;
  }
}

@media screen and (max-width: 1000px) {
  #map {
    display: grid;
    grid-template-columns: auto max-content;
    grid-template-rows: auto max-content;
  }

  .map-overlay {
    margin-right: unset;
    position: unset;
  }

  div.selectors {
    margin-left: unset;
    margin-top: unset;
    grid-row: 1;
    grid-column: 2;
  }

  .mapboxgl-map {
    grid-row-start: 1;
    grid-row-end: 3;
    grid-column: 1;
  }

  #legend {
    grid-row: 2;
    grid-column: 2;
    margin: 0;
  }
}

@media screen and (max-width: 576px) {
  #map {
    display: grid;
    grid-template-rows: auto max-content max-content;
    grid-template-columns: auto;
  }

  div.selectors {
    grid-row: 1;
    grid-column: 1;
  }

  .mapboxgl-map {
    grid-row: 2;
    height: 75vh;
  }

  #legend {
    grid-row: 3;
    grid-column: 1;
  }

  .shift_layer_control {
    margin: 734px 0 0 0;
  }

}
</style>
